import React, { useState, useEffect } from "react";
import Pagination from "@/components/pagination";
import SearchInput from "@/components/search-input";
import API from "@/services/api";
import toast from "react-hot-toast";
import OrganizationModal from "../components/OrganizationModal";

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({
    approved: undefined,
    search: "",
  });
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const limit = 25;

  useEffect(() => {
    fetchOrganizations();
  }, [currentPage, values]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await API.post("/organization/search", {
        page: currentPage,
        limit: limit,
        ...values,
      });

      if (!response.ok) throw response;
      setOrganizations(response.data || []);
      setTotal(response.total || 0);
    } catch (e) {
      console.error("Error fetching organizations:", e);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  const handleViewRow = (org) => {
    setSelectedOrganization(org);
  };

  return (
    <div className="p-6">
      {/* Table Controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <SearchInput
            value={values.search}
            onChange={(value) => {
              setValues({ ...values, search: value });
              setCurrentPage(1);
            }}
            className="w-64"
          />
          <div className="flex gap-2">
            <button
              className={`px-3 py-1 rounded border text-sm font-medium ${
                values.approved === undefined ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-600 hover:bg-blue-50"
              }`}
              onClick={() => {
                setValues({ ...values, approved: undefined });
                setCurrentPage(1);
              }}
            >
              All
            </button>
            <button
              className={`px-3 py-1 rounded border text-sm font-medium ${
                values.approved === true ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-600 hover:bg-blue-50"
              }`}
              onClick={() => {
                setValues({ ...values, approved: true });
                setCurrentPage(1);
              }}
            >
              Approved
            </button>
            <button
              className={`px-3 py-1 rounded border text-sm font-medium ${
                values.approved === false ? "bg-blue-600 text-white border-blue-600" : "bg-white text-blue-600 border-blue-600 hover:bg-blue-50"
              }`}
              onClick={() => {
                setValues({ ...values, approved: false });
                setCurrentPage(1);
              }}
            >
              Not Approved
            </button>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Total: <span className="font-semibold text-gray-900">{total}</span>
          </div>
          <Pagination total={total} limit={limit} currentPage={currentPage} onPageChange={setCurrentPage} />
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : organizations.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No organizations found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Is approved</th>
                  <th className="px-4 py-3">Created Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {organizations.map((org) => {
                  const orgId = org._id || org.id;
                  return (
                    <tr key={orgId} onClick={() => handleViewRow(org)} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">{org.name || "-"}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{org.approved ? "Yes" : "No"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{org.created_at ? new Date(org.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OrganizationModal isOpen={!!selectedOrganization} onClose={() => setSelectedOrganization(null)} organization={selectedOrganization} fetchData={fetchOrganizations} />
    </div>
  );
};

export default Organizations;
